/* src/pages/articles/ArticleDetailPage.js (Simpler Menu Fix) */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const ArticleDetailPage = () => {
    // --- NEW: State management for the menu, local to this page ---
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [commentList, setCommentList] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [relatedVideos, setRelatedVideos] = useState([]);
    const [upcomingCourse, setUpcomingCourse] = useState(null);
    const [user, setUser] = useState(null);

    // Debug: Log the id
    console.log('ArticleDetailPage - id:', id);
    
    const getAuthInfo = () => {
        const token = localStorage.getItem('app_token');
        if (!token) return { isLoggedIn: false, userId: null, token: null, decoded: null };
        try {
            const decoded = jwtDecode(token);
            return { isLoggedIn: true, userId: decoded.googleId, token, decoded };
        } catch (error) {
            console.error("Invalid token:", error);
            return { isLoggedIn: false, userId: null, token: null, decoded: null };
        }
    };

    useEffect(() => {
        // Don't fetch if id is undefined
        if (!id) {
            console.error('ArticleDetailPage - id is undefined');
            setError("Article ID is missing");
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            const { isLoggedIn, token, decoded } = getAuthInfo();
            
            // Set user state if logged in
            if (isLoggedIn && decoded) {
                setUser(decoded);
            }
            
            try {
                const [articleRes, commentsRes] = await Promise.all([
                    axios.get(`/api/articles/${id}`),
                    axios.get(`/api/articles/${id}/comments`)
                ]);
                const mainArticle = articleRes.data;
                if (!mainArticle) throw new Error('Article not found.');
                
                setArticle(mainArticle);
                setCommentList(commentsRes.data);

                if (isLoggedIn) {
                    const statusRes = await axios.get(`/api/bookmarks/status/article/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                    setIsBookmarked(statusRes.data.isBookmarked);
                }
                if (mainArticle.category) {
                    try {
                        const [relatedArticlesRes, relatedVideosRes] = await Promise.all([
                            axios.get(`/api/articles/summary_list_cat/${mainArticle.category}`),
                            axios.get(`/api/videos/summary_list_cat/${mainArticle.category}`)
                        ]);
                        // Fix: handle paginated response for articles
                        const relatedArticlesData = relatedArticlesRes.data.items || relatedArticlesRes.data;
                        const relatedArticlesArray = Array.isArray(relatedArticlesData) ? relatedArticlesData : [];
                        setRelatedArticles(relatedArticlesArray.filter(a => a.id !== mainArticle.id).slice(0, 2));
                        // Fix: handle paginated response for videos
                        const relatedVideosData = relatedVideosRes.data.items || relatedVideosRes.data;
                        const relatedVideosArray = Array.isArray(relatedVideosData) ? relatedVideosData : [];
                        setRelatedVideos(relatedVideosArray.slice(0, 2));
                    } catch (relatedError) {
                        console.error("Failed to fetch related content:", relatedError);
                        setRelatedArticles([]);
                        setRelatedVideos([]);
                    }
                }
            } catch (err) {
                setError(err.message || "Failed to load article data.");
                console.error("Fetch data error:", err);
            } finally {
                setIsLoading(false);
            }
        };
        const fetchCourses = async () => {
            try {
                const response = await axios.get('/api/courses');
                const liveCourse = response.data.find(course => course.start_date);
                setUpcomingCourse(liveCourse);
            } catch (error) { console.error("Failed to fetch courses:", error); }
        };
        fetchData();
        fetchCourses();
    }, [id]);

    const handleBookmarkToggle = async () => {
        const { isLoggedIn, token } = getAuthInfo();
        if (!isLoggedIn) { navigate('/login'); return; }
        setIsBookmarked(prev => !prev);
        try {
            await axios.post('/api/bookmarks/toggle', { contentId: id, contentType: 'article' }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (error) {
            console.error("Failed to toggle bookmark", error);
            setIsBookmarked(prev => !prev);
            alert("Failed to update bookmark.");
        }
    };
    
    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: article.title, text: `Check out this article: ${article.title}`, url: window.location.href });
            } catch (error) { console.error('Error sharing:', error); }
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        const { isLoggedIn, token, decoded } = getAuthInfo();
        if (!isLoggedIn) { navigate('/login'); return; }
        if (!newComment.trim()) return;
        try {
            const response = await axios.post(`/api/articles/${id}/comments`, { commentText: newComment }, { headers: { Authorization: `Bearer ${token}` } });
            const newlyCreatedComment = { id: response.data.id, comment_text: newComment, name: decoded.name, profile_picture: decoded.profilePicture, created_at: new Date().toISOString() };
            setCommentList([newlyCreatedComment, ...commentList]);
            setNewComment('');
        } catch (error) {
            console.error("Failed to post comment:", error);
            alert("Could not post your comment. Please try again.");
        }
    };

    const getHeaderTitle = () => {
        if (isLoading) return "Loading...";
        return article.title || "Article";
    };

    if (isLoading || !article) {
        return (
            <>
                {isLoading && <p>Loading article...</p>}
                {error && <p className="error-message">{error}</p>}
            </>
        );
    }

    return (
        <>
            <div className="article-detail-page">
                 <div className="article-header-image" style={{ backgroundImage: `url(${article.banner_image_url})`, position: 'relative' }}>
                    {/* Add a dark gradient overlay for readability */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(180deg,rgba(0,0,0,0.45) 0%,rgba(0,0,0,0.15) 100%)',
                      zIndex: 1
                    }} />
                    <div className="article-header-overlay" style={{ position: 'relative', zIndex: 2 }}>
                        <h1 className="article-main-title" style={{ color: '#fff', textShadow: '0 2px 12px #000b, 0 1px 2px #000b', margin: 0 }}>{article.title}</h1>
                    </div>
                </div>

                <section className="interaction-bar" style={{marginTop: '1.5rem'}}>
                    <button onClick={handleBookmarkToggle} className="interaction-button">
                        <span className={`material-icons-outlined ${isBookmarked ? 'bookmarked' : ''}`}>{isBookmarked ? 'bookmark' : 'bookmark_border'}</span>
                        <span>{isBookmarked ? 'Saved' : 'Save'}</span>
                    </button>
                    <button onClick={handleShare} className="interaction-button">
                        <span className="material-icons-outlined">share</span>
                        <span>Share</span>
                    </button>
                </section>
                
                <div className="article-content-body">
                    <div className="article-tags">
                      {(article.tags ? article.tags.split(',') : []).map((tag, index) => (<span key={index} className="article-tag">{tag.trim()}</span>))}
                    </div>
                    
                    {article.summary && (
                      <div className="article-summary">
                        <h3 className="summary-title">What You Will Learn:</h3>
                        <div className="summary-list" dangerouslySetInnerHTML={{ __html: article.summary }} />
                      </div>
                    )}

                    <div className="article-text-content" dangerouslySetInnerHTML={{ __html: article.content }} />
                    
                    <div className="article-footer">For more insights, please follow <Link to="/profile" className="author-link">{article.author_name}</Link>.</div>
                </div>
                    {/* Add improved formatting styles */}
                    <style>{`
                      .article-main-title {
                        font-size: 2.6rem;
                        font-weight: 800;
                        color: #2d2d2d;
                        margin: 0 0 1.2rem 0;
                        letter-spacing: -1px;
                        text-shadow: 0 2px 8px #0001;
                      }
                      .article-content-body h1 {
                        font-size: 2rem;
                        font-weight: 700;
                        margin: 2.2rem 0 1.2rem 0;
                        color: #3a2e6c;
                      }
                      .article-content-body h2 {
                        font-size: 1.5rem;
                        font-weight: 600;
                        margin: 2rem 0 1rem 0;
                        color: #4b3ca7;
                      }
                      .article-content-body h3 {
                        font-size: 1.2rem;
                        font-weight: 600;
                        margin: 1.5rem 0 0.8rem 0;
                        color: #5c4db1;
                      }
                      .article-summary {
                        background: #f6f6ff;
                        border-left: 4px solid #6c47ff;
                        padding: 1.2rem 1.5rem;
                        border-radius: 8px;
                        margin: 1.5rem 0 2rem 0;
                      }
                      .summary-title {
                        font-size: 1.1rem;
                        font-weight: 700;
                        color: #6c47ff;
                        margin-bottom: 0.5rem;
                      }
                      .summary-list {
                        font-size: 1rem;
                        color: #333;
                      }
                      .article-content-body p {
                        font-size: 1.08rem;
                        line-height: 1.8;
                        margin: 1.1em 0;
                      }
                      .article-content-body ul, .article-content-body ol {
                        margin: 1em 0 1em 2em;
                      }
                    `}</style>
                    
                    <section className="comments-section">
                        <h3 className="section-title">Comments ({commentList.length})</h3>
                        <form onSubmit={handleCommentSubmit} className="comment-form">
                            <input type="text" placeholder="Add a comment..." className="comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} />
                            <button type="submit" className="post-comment-button">Post</button>
                        </form>
                        <div className="comments-list">
                            {commentList.map(comment => (
                                <div key={comment.id} className="comment-item">
                                    <img src={comment.profile_picture || 'https://placehold.co/40x40/16a085/ffffff?text=U'} alt={comment.name} className="comment-author-pic" />
                                    <div className="comment-body">
                                        <Link to={`/profile/${comment.user_id}`} className="comment-author-name" style={{textDecoration: 'none'}}>{comment.name}</Link>
                                        <p className="comment-text">{comment.comment_text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
                
                 <div className="related-content-container">
                    {upcomingCourse && user && user.role === 'admin' && (
                        <section className="related-section">
                            <h3 className="section-title">Upcoming Course</h3>
                            <div className="upcoming-course-card">
                                <div className="course-card-text">
                                    <h4>{upcomingCourse.title}</h4>
                                    <p>A new live workshop is coming soon. Stay tuned!</p>
                                    <Link to="/courses" className="course-preview-button"><span className="material-icons">event</span><span>View Courses</span></Link>
                                </div>
                                <img src={upcomingCourse.img} alt={upcomingCourse.title} className="course-card-image" />
                            </div>
                        </section>
                    )}
                    
                    <section className="related-section">
                        <h3 className="section-title">Related Videos</h3>
                        <div className="content-list">
                          {relatedVideos.length > 0 ? (
                            relatedVideos.map((item) => (<Link to={`/videos/${item.id}`} key={item.id} className="content-card"><img src={item.thumbnail_url} alt={item.title} className="content-card-thumbnail" /><div className="content-card-body"><h3 className="content-card-title">{item.title}</h3><p className="content-card-description">{item.category}</p></div></Link>))
                          ) : (<p className="no-related-content">No related videos found.</p>)}
                        </div>
                    </section>
                    
                    <section className="related-section">
                        <h3 className="section-title">Related Articles</h3>
                        <div className="content-list">
                          {relatedArticles.length > 0 ? (
                            relatedArticles.map((item) => (<Link to={`/articles/${item.id}`} key={item.id} className="content-card"><img src={item.banner_image_url} alt={item.title} className="content-card-thumbnail" /><div className="content-card-body"><h3 className="content-card-title">{item.title}</h3><p className="content-card-description">{item.category}</p></div></Link>))
                          ) : (<p className="no-related-content">No other articles found.</p>)}
                        </div>
                    </section>
                  </div>
            </>
        );
};

export default ArticleDetailPage;
